import { useState, useMemo, useCallback }  from "react";
import { createPortal }                     from "react-dom";
import { STAGES, GLOBAL_CSS }              from "./components/constants";
import { useTeamMembers, useCurrentUser } from "../../../hooks/useTeamMembers";
import { useOpportunities }                from "../../../hooks/useOpportunities";
import CrmHeader                           from "./components/CrmHeader";
import FilterBar                           from "./components/FilterBar";
import CrmKanban                           from "./components/CrmKanban";
import CrmList                             from "./components/CrmList";
import CreateOpportunityDrawer             from "./components/CreateOpportunityDrawer";
import OpportunityDetailsDrawer            from "./components/OpportunityDetailsDrawer";

export default function CrmApp() {
  const { teamMembers }   = useTeamMembers();
  const { currentUser }   = useCurrentUser();
  const {
    opps, loading,
    createOpp, updateOpp, deleteOpp, archiveOpp, restoreOpp, changeStage,
  } = useOpportunities();

  const [view,        setView]        = useState("kanban");
  const [activeTab,   setActiveTab]   = useState("kanban");
  const [pipeline,    setPipeline]    = useState("Management Consulting");
  const [showCreate,  setShowCreate]  = useState(false);
  const [selected,    setSelected]    = useState(null);
  const [filters,     setFilters]     = useState({ search: "", pipeline: "", stage: "", assignee: "", priority: "" });
  const [sortBy,      setSortBy]      = useState("name");
  const [quickFilter, setQuickFilter] = useState("");

  // ── Filtered + sorted ──
  const filtered = useMemo(() => opps
    .filter(o => {
      if (activeTab === "archived" && !o.archived)                           return false;
      if ((activeTab === "active" || activeTab === "kanban") && o.archived)  return false;
      if (filters.search) {
        const q = filters.search.toLowerCase();
        if (!o.name?.toLowerCase().includes(q) &&
            !o.company?.toLowerCase().includes(q) &&
            !o.contact?.toLowerCase().includes(q)) return false;
      }
      if (filters.pipeline && o.pipeline !== filters.pipeline) return false;
      if (filters.stage    && o.stage    !== filters.stage)    return false;
      if (filters.assignee && o.assignee?.name !== filters.assignee) return false;
      if (filters.priority && o.priority !== filters.priority) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "amount")      return (b.amount || 0) - (a.amount || 0);
      if (sortBy === "stage")       return STAGES.indexOf(a.stage) - STAGES.indexOf(b.stage);
      if (sortBy === "endDate")     return (a.endDate || "").localeCompare(b.endDate || "");
      if (sortBy === "probability") return (b.probability || 0) - (a.probability || 0);
      return (a.name || "").localeCompare(b.name || "");
    }),
  [opps, activeTab, filters, sortBy]);

  // ── Handlers ──
  const handleCreate = useCallback(async (data) => {
    try { await createOpp(data); } catch (e) { console.error("[CRM] Create:", e.message); }
    setShowCreate(false);
  }, [createOpp]);

  const handleUpdate = useCallback(async (updated) => {
    setSelected(updated); // optimistic
    try { await updateOpp(updated); } catch (e) { console.error("[CRM] Update:", e.message); }
  }, [updateOpp]);

  const handleDelete = useCallback(async (id) => {
    setSelected(null);
    try { await deleteOpp(id); } catch (e) { console.error("[CRM] Delete:", e.message); }
  }, [deleteOpp]);

  const handleArchive = useCallback(async (opp) => {
    try { await archiveOpp(opp); } catch (e) { console.error("[CRM] Archive:", e.message); }
  }, [archiveOpp]);

  const handleRestore = useCallback(async (opp) => {
    try { await restoreOpp(opp); } catch (e) { console.error("[CRM] Restore:", e.message); }
  }, [restoreOpp]);

  const handleStage = useCallback(async (id, stage) => {
    try { await changeStage(id, stage, currentUser); } catch (e) { console.error("[CRM] Stage:", e.message); }
  }, [changeStage, currentUser]);

  const openCreate  = useCallback(() => setShowCreate(true),  []);
  const closeCreate = useCallback(() => setShowCreate(false), []);

  return (
    // crm-root → all GLOBAL_CSS rules are scoped to this class (no global bleed to sidebar)
    <div className="crm-root" style={{ background: "#f4f4f9", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <style>{GLOBAL_CSS}</style>

      {/* Loading bar — pointerEvents:none so it NEVER blocks clicks */}
      {loading && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: "3px", zIndex: 9999, pointerEvents: "none" }}>
          <div style={{ height: "100%", background: "linear-gradient(90deg,#3761E9,#6c4fff)", animation: "pulse 1.2s infinite" }} />
        </div>
      )}

      <CrmHeader
        view={view}           setView={setView}
        activeTab={activeTab} setActiveTab={setActiveTab}
        onCreateNew={openCreate}
        total={filtered.length}
        pipeline={pipeline}   setPipeline={setPipeline}
      />

      <FilterBar
        filters={filters}         setFilters={setFilters}
        sortBy={sortBy}           setSortBy={setSortBy}
        opps={opps}               teamMembers={teamMembers}
        quickFilter={quickFilter} setQuickFilter={setQuickFilter}
      />

      {view === "kanban" ? (
        <CrmKanban
          opps={filtered}
          onCardClick={setSelected}
          onCreateNew={openCreate}
          onEditOpp={setSelected}
          onDeleteOpp={handleDelete}
          onArchiveOpp={handleArchive}
          onStageChange={handleStage}
        />
      ) : (
        <CrmList
          opps={filtered}
          allOpps={opps}
          activeTab={activeTab}
          onRowClick={setSelected}
          onEdit={setSelected}
          onDelete={handleDelete}
          onArchive={id => { const o = opps.find(x => x.id === id); if (o) handleArchive(o); }}
          onRestore={handleRestore}
        />
      )}

      {/* ── Drawers rendered via Portal directly on document.body ──
          This bypasses any overflow:hidden / stacking context from admin layout
          and guarantees the drawers always appear above everything.          */}
      {showCreate && createPortal(
        <CreateOpportunityDrawer
          onClose={closeCreate}
          onCreate={handleCreate}
          teamMembers={teamMembers}
        />,
        document.body
      )}

      {selected && createPortal(
        <OpportunityDetailsDrawer
          key={selected.id}
          opp={selected}
          onClose={() => setSelected(null)}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onStageChange={handleStage}
          teamMembers={teamMembers}
        />,
        document.body
      )}
    </div>
  );
}
